import React, {createContext, useContext, useState, useCallback, useEffect, ReactNode} from 'react';
import {reactCreateMemo} from '@wix/devzai-utils-react';
import {
    bymoPagePropsHasInnerPagesFeature
} from '../../client-server-common';
import {BymoPagePropsForViewer} from '../../client-server-common/bymo-pages';

export interface BymoPageNavigationContextValue {
    /**
     * The current active page ID, or null if on home page
     */
    currentPageId: string | null;
    
    /**
     * Change the current active page
     */
    onPageChange: (pageId: string | null) => void;
    
    /**
     * Whether the current page props support multi-page navigation
     */
    isMultiPage: boolean;
    
    /**
     * The home page ID for the current page props
     */
    homePageId?: string | null;
}

const BymoPageNavigationContext = createContext<BymoPageNavigationContextValue | null>(null);

export interface BymoPageNavigationProviderProps {
    children: ReactNode;
    bymoPageProps: BymoPagePropsForViewer;
    initialPageId?: string | null;
    onPageChange?: (pageId: string | null) => void;
}

/**
 * Provider for page navigation context. This should be placed at the top level
 * of the page rendering tree to manage current page state.
 */
export const BymoPageNavigationProvider = reactCreateMemo(function BymoPageNavigationProvider(
    props: BymoPageNavigationProviderProps
) {
    const {
        children,
        bymoPageProps,
        initialPageId = null,
        onPageChange: externalOnPageChange
    } = props;
    
    const isMultiPage = bymoPagePropsHasInnerPagesFeature(bymoPageProps);
    
    // Internal state for current page
    const [currentPageId, setCurrentPageId] = useState<string | null>(initialPageId);
    
    // Update currentPageId when initialPageId changes
    useEffect(() => {
        setCurrentPageId(initialPageId ?? null);
    }, [initialPageId]);
    
    // Handle page changes
    const handlePageChange = useCallback((pageId: string | null) => {
        setCurrentPageId(pageId);
        externalOnPageChange?.(pageId);
    }, [externalOnPageChange]);
    
    const contextValue: BymoPageNavigationContextValue = {
        currentPageId,
        onPageChange: handlePageChange,
        isMultiPage
    };
    
    return (
        <BymoPageNavigationContext.Provider value={contextValue}>
            {children}
        </BymoPageNavigationContext.Provider>
    );
});

/**
 * Hook to access the page navigation context.
 * Must be used within a BymoPageNavigationProvider.
 */
export function useBymoPageNavigation(): BymoPageNavigationContextValue {
    const context = useContext(BymoPageNavigationContext);
    
    if (!context) {
        throw new Error('useBymoPageNavigation must be used within a BymoPageNavigationProvider');
    }
    
    return context;
}

/**
 * Hook to access the page navigation context, returning null if not available.
 * This is useful for components that can work both with and without navigation context.
 */
export function useBymoPageNavigationOptional(): BymoPageNavigationContextValue | null {
    return useContext(BymoPageNavigationContext);
}
